<template>
	<div class="cat-selector game-screen paw-bg">
		<transition name="fade">
			<ArrowButton :action="unInspect" v-show="isInspecting">{{ ui.back_button_text }}</ArrowButton>
		</transition>
		<h1 class="heading" :class="{faded: isInspecting}">{{ ui.heading }}</h1>
		<div class="cat-list" :class="{ inspecting : isInspecting }">
			<div class="cat-list-item"
				v-for="breed in profiles"
				:key="breed.name"
				:ref="'profiles'"
				:class="inspectionClasses(breed)"
				@click="inspect(breed)"
			>
				<CatProfile :settings="breed" :beingInspected="isInspectingCat(breed)" />
			</div>
		</div>
	</div>
</template>
<script>
import CatProfile from '@/components/cat/CatProfile';
import ArrowButton from '@/components/abstract/ArrowButton';
import soundFx from '@/mixins/sound-fx';
import { precisionRound } from "@/mixins/precision-round";
import { mapState, mapActions } from 'vuex';

export default {
	name: 'CatSelector',
	mixins: [soundFx],
	components: {
		CatProfile,
		ArrowButton,
	},
	data() {
		return {
			profiles: [],
			inspectedProfile: null,
		};
	},
	computed: {
		...mapState({
			breeds: state => state.config.breeds,
			ui: state => state.config.ui.cat_selector,
		}),
		isInspecting() {
			return this.inspectedProfile !== null;
		},
		mixedLitterSettings() {
			return {
				key: "mixed_litter",
				name: "Mixed Litter",
				info: this.$store.state.config.mixed_litter.info,
				photo: this.$store.state.config.mixed_litter.profile_photo,
				food_required: "??",
				spawn_increase_percentage: "??",
				background_color: this.$store.state.config.mixed_litter.background_color,
				profile_background: this.$store.state.config.mixed_litter.profile_background,
				breedIndexes: [...Array(this.breeds.length).keys()],
			};
		},
	},
	methods: {
		...mapActions(["sendEvent"]),
		inspect(cat) {
			this.inspectedProfile = cat;
			this.sendEvent({
				ec: "Pre-Game",
				ea: "Inspect",
				el: cat.name,
			});
			this.playRandomSound("swooshes");
		},
		isInspectingCat(cat) {
			return this.inspectedProfile === cat;
		},
		unInspect() {
			this.inspectedProfile = null;
		},
		inspectionClasses(cat) {
			let className = "";

			if (this.inspectedProfile) {
				className = "inspecting-other";
			}

			if (this.isInspectingCat(cat)) {
				className = "inspecting";
			}

			return className;
		},
	},
	mounted() {
		this.breeds.forEach((breed, index) => {
			this.profiles.push({
				key: breed.name,
				name: breed.title,
				info: breed.info,
				photo: breed.profile_photo,
				food_required: breed.food_required,
				spawn_increase_percentage: precisionRound(breed.spawn_increase_percentage * 100, 0),
				background_color: breed.background_color,
				profile_background: breed.profile_background,
				breedIndexes: [index],
			});
		});

		this.profiles.push(this.mixedLitterSettings);
	},
};
</script>

<style lang="scss" scoped>
	.cat-selector {
		flex-direction: column;
	}

	.heading {
		position: absolute;
		top: 5%;
		margin: 0;
		left: 0;
		width: 100%;

		line-height: 1;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: .07em;
		font-size: 50px;
		text-align: center;

		transition: opacity $ui-transition-duration;
		opacity: 1;

		&.faded {
			opacity: 0;
		}

		@include mobile {
			width: 100%;
			top: 2.5%;
			font-size: 32px;
			text-align: center;
			left: 0;
		}
	}

	.cat-list {
		width: 80%;
		height: 80%;
		position: relative;
		transition: all $ui-transition-duration;
		margin-top: 25px;

		@include mobile {
			width: 90%;
			height: 90%;
			position: absolute;
			top: 10%;
			left: 5%;
			margin-top: 0;

			&.inspecting {
				top: 2%;
			}
		}
	}

	.cat-list-item {
		display: block;
		position: absolute;
		padding: 2vh 2vw;
		width: 33.33%;
		transform: translate3d(0, 0, 0);

		left: 0;
		transition: all $ui-transition-duration;

		cursor: pointer;
		transition-delay:  $ui-transition-duration;

		@include desktop-sm {
			&:nth-child(-n + 6) {
				top: 50%;
			}

			&:nth-child(-n + 3) {
				top: 0;
			}

			&:nth-child(3n + 2) {
				left: 33.33%;
			}

			&:nth-child(3n + 3) {
				left: 66.66%;
			}
		}

		@include mobile {
			width: 50%;
			left: 0;

			top: 66.66%;

			&:nth-child(-n + 4) {
				top: 33.33%;
			}

			&:nth-child(-n + 2) {
				top: 0;
			}

			&:nth-child(2n + 2) {
				left: 50%;
			}
		}

		/deep/ .profile-photo {
			@include desktop-sm {
				max-width: 250px;
			}

			@include sm-vertical() {
				max-width: 25vh;
				margin: 0 auto;
			}

			@include x-sm-vertical {
				max-width: 25vmin;
			}

			@include mobile() {
				max-width: 25vmin;
			}
		}

		&.inspecting-other {
			opacity: 0;
			z-index: 0;
			transition-delay: 0s;
			pointer-events: none;
		}

		&.inspecting {
			width: 100%;

			top: 50% !important;
			left: 0 !important;
			transform: translate3d(0, -50%, 0);

			transition-delay: 0s;
			cursor: auto;

			/deep/ .profile-photo {
				max-width: 300px;

				@include mobile {
					max-width: 150px;
				}

				@include sm-vertical() {
					max-width: 35vmin;
				}

				@include x-sm-vertical() {
					max-width: 20vh;
				}

				@include tablet {
					max-width: 25vmin;
				}
			}
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/views/CatSelector.vue